import { mapState, mapActions } from 'vuex';
import Compressor from 'compressorjs';
import { isIOS, isIpad } from '@utiles/device';
import bridge from '@utiles/bridge';
import globalConfig from '@utiles/globalConfig';
import { isAndroid } from '@utiles/device';

/**
 * 文件转为base64
 * @param {*} file 文件对象
 * @returns
 */
export const fileToBinary = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      resolve(event?.target?.result || '');
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * base64转为文件
 * @param {*} base64String base64字符串
 * @param {*} fileName 文件名字：xxx.mp4
 * @param {*} fileType 文件类型：video/mp4
 * @returns
 */
export const binaryToFile = (base64String, fileName, fileType) => {
  // 去除base64前缀
  base64String = base64String.replace(
    /^data:(image|video)\/(png|jpg|jpeg|mp4);base64,/,
    ''
  );

  // 将 base64 字符串转换为字节数组
  var byteCharacters = atob(base64String);

  // 创建一个 Uint8Array，用于存储字节数组
  var byteArrays = new Uint8Array(byteCharacters.length);
  for (var i = 0; i < byteCharacters.length; i++) {
    byteArrays[i] = byteCharacters.charCodeAt(i);
  }

  // 创建一个 Blob 对象，将字节数组作为 Blob 的内容
  var blob = new Blob([byteArrays], { type: fileType });

  // 创建一个文件对象，将 Blob 对象作为文件的内容
  var file = new File([blob], fileName, { type: fileType });

  return Promise.resolve(file);
};

export const acceptType = {
  image: 'image/jpg,image/png',
  video: 'video/*',
  imageAndVideo: 'image/jpg,image/png,video/*'
};

export default {
  data() {
    const openVideoUploadFunction =
      bridge.runInsdk() &&
      globalConfig.getHrefParam('isSupportVideoCompress') == 1;
    const accept = openVideoUploadFunction
      ? acceptType.imageAndVideo // 客户端环境，有视频上传
      : acceptType.image; // 浏览器环境，无视频上传
    const VIDEO_CONFIG = {
      presetLevel: '1', // 输出视频分辨率等级LOW=1(960*720)， MEDIUM=2(1280*960),HIGH=3(1920*1080)。传数字：123
      bitRateLevel: '3', // VERY_LOW=1, LOW=2, NORMAL=3, HIGH=4, VERY_HIGH=5。传数字：12345
      bitRateInMbps: '', // 自定义视频码率，优先级大于bitRateLevel。如3500*1000bps传字符串：3.5
      maxDuration: '2', // 视频最大时长，单位分钟，传数字。如：2分钟传2
      maxSize: '400', // 视频最大文件，单位MB，传数字。如:20M传20
      compressLoadingColor: '#5bafff' // 压缩视频时的loading颜色，传颜色值。如：#5bafff
    };

    const isIOSUpload = (isIOS() || isIpad()) && openVideoUploadFunction;

    // TODO:DEVtest
    // const isIOSUpload = false;

    return {
      VIDEO_CONFIG, // 视频压缩配置
      accept, // 接受的文件类型
      openVideoUploadFunction, // 是否支持视频压缩上传
      isIOSUpload, // 是否是iOS上传
      otherMaxCount: 3, // 最大上传数量
      iOSMaxCount: 0, // iOS最大上传数量
      workFileArr: [], // 上传的文件列表
      canUseUpload: !(globalConfig.isTiktokMiniProgram() && isAndroid())
    };
  },
  computed: {
    ...mapState({
      languages: state => state.languages
    }),
    maxCount() {
      return this.isIOSUpload ? this.iOSMaxCount : this.otherMaxCount;
    },
    showIOSUpload() {
      return this.isIOSUpload && this.workFileArr.length < this.otherMaxCount;
    }
  },
  created() {
    this.androidVideoInitConfig();
  },
  methods: {
    ...mapActions(['fragmentationUploadImage', 'fragmentationUploadVideo']),
    // 安卓初始化视频压缩配置
    androidVideoInitConfig() {
      // 安卓视频压缩配置
      if (this.openVideoUploadFunction) {
        try {
          bridge.callSdkMethod('compressVideoConfig', this.VIDEO_CONFIG);
        } catch (error) {
          console.error(error);
        }
      }
    },
    // 视频上传完成告诉安卓可以删除压缩后的视频了
    callAndroidDelVideo() {
      try {
        bridge.callSdkMethod('clearVideoCache');
      } catch (error) {
        console.error(error);
      }
    },
    // 检查文件大小
    checkFileSize(file, maxSizeInMB) {
      const fileSizeInBytes = file.size;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

      if (fileSizeInBytes > maxSizeInBytes) {
        return true; // 文件大小超出限制
      } else {
        return false; // 文件大小在限制范围内
      }
    },
    // 检查视频文件时长
    async checkVideoFileDuration(file, seconds) {
      try {
        const duration = await this.getVideoDuration(file);
        // 返回true表示视频时长超出限制，false表示视频时长在限制范围内
        return Promise.resolve(duration > seconds);
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      }
    },
    // 检查文件是否是视频
    checkFileIsVideo(file) {
      return file?.type?.includes('video');
    },
    // 大小超出提示，true表示超出限制，false表示未超出限制
    async oversize(file) {
      if (file.type.includes('video')) {
        // 前端判断：视频大小判断
        // if (
        //   this.checkFileSize(file, 400) ||
        //   (await this.checkVideoFileDuration(file, 120))
        // ) {
        //   this.$toast(this.languages?.videoUploadLimit);
        //   return true;
        // }

        // SDK来判断
        return false;
      } else {
        // 图片大小判断
        if (this.checkFileSize(file, 20)) {
          this.$toast(this.languages?.creatework_uploadTips);
          return true;
        }
      }
      return false;
    },
    // 图片压缩
    async compressImage(file) {
      //  file 如果是一张是个对象，如果是多个是个数组
      let fileArr = [];
      let newFileArr = [];
      if (file.length) {
        fileArr = file;
      } else {
        fileArr.push(file);
      }
      for (let i = 0, len = fileArr.length; i < len; i++) {
        const fileItem = fileArr[i];
        if (fileItem.size > 4 * 1024 * 1024) {
          await new Promise(resolve => {
            try {
              new Compressor(
                new window.File([file], file.name, { type: file.type }),
                {
                  quality: 0.7,
                  success(miniResultBlob) {
                    const compressFile = new window.File(
                      [miniResultBlob],
                      file.name,
                      { type: file.type }
                    );
                    compressFile.isCompress = 1;
                    newFileArr.push(compressFile);
                    resolve();
                  },
                  error(err) {
                    console.error(err.message);
                    resolve();
                  }
                }
              );
            } catch (error) {
              console.error(error);
              compressFile.isCompress = 0;
              newFileArr.push(compressFile);
              resolve();
            }
          });
        } else {
          fileItem.isCompress = 0;
          newFileArr.push(fileItem);
        }
      }
      return Promise.resolve(newFileArr);
    },
    // 视频时长格式转换,如果秒数小于10，前面补0
    formatVideoDuration(seconds) {
      var minutes = Math.floor(seconds / 60);
      var remainingSeconds = Math.round(seconds % 60);
      if (remainingSeconds < 10) {
        remainingSeconds = '0' + remainingSeconds;
      }
      return minutes + ':' + remainingSeconds;
    },
    // 传入视频文件对象获取视频的时长
    getVideoDuration(file) {
      return new Promise(resolve => {
        const videoElement = document.createElement('video');
        // 坑爆了，谷歌浏览器要加这两个属性才能获取到视频的第一帧
        videoElement.setAttribute('preload', 'auto');
        videoElement.setAttribute('autoplay', 'true');
        videoElement.setAttribute('muted', 'true');
        videoElement.onloadeddata = () => {
          setTimeout(() => {
            videoElement.pause();
            resolve(videoElement.duration);
          }, 100);
        };
        videoElement.src = URL.createObjectURL(file);
        // 必须调用 load 方法来触发加载视频
        videoElement.load();
      });
    },
    // 获取视频信息
    getVideoPlayerInfo(file) {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = event => {
          const videoElement = document.createElement('video');
          // 坑爆了，谷歌浏览器要加这两个属性才能获取到视频的第一帧
          videoElement.setAttribute('preload', 'auto');
          videoElement.setAttribute('autoplay', 'true');
          videoElement.setAttribute('muted', 'true');
          videoElement.onloadeddata = () => {
            setTimeout(() => {
              videoElement.pause();
              const canvas = document.createElement('canvas');
              canvas.width = videoElement.videoWidth;
              canvas.height = videoElement.videoHeight;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
              // 将 canvas 转换为 base64 字符串
              const base64String = canvas.toDataURL('image/jpeg', 0.1);
              resolve({
                element: videoElement,
                firstFrameBase64Str: base64String,
                duration: this.formatVideoDuration(videoElement.duration),
                width: videoElement.videoWidth,
                height: videoElement.videoHeight
              });
            }, 100);
          };
          videoElement.src = event.target.result;
          // 必须调用 load 方法来触发加载视频
          videoElement.load();
        };
        // 读取文件为 DataURL
        reader.readAsDataURL(file);
      });
    },
    // 上传图片前的处理
    beforeReadImage(file) {
      // 图片上传处理：file 如果是一张是个对象，如果是多个是个数组
      return new Promise(async resolve => {
        const fileArr = await this.compressImage(file);
        resolve(fileArr);
        // reject(file); 什么都不会发生，需要自定义一些错误处理，比如弹窗提示
      });
    },
    // 上传前的处理
    async beforeRead(file) {
      if (await this.oversize(file)) {
        return Promise.reject();
      }
      // 视频上传处理
      if (this.checkFileIsVideo(file)) {
        // 如果不是webview环境，input的accept有时不可靠，需要执行一下检查。只允许图片的情况下有的机子还能选视频。
        if (this.openVideoUploadFunction) {
          // 检查用户当天是否达到最大上传限制
          // const boolCanUploadVideo = await this.canUploadVideo();
          // if (boolCanUploadVideo) {
          //   return Promise.resolve([file]);
          // } else {
          //   this.$toast('当天上传视频数量已达上限');
          //   return Promise.reject();
          // }
          return Promise.resolve([file]);
        } else {
          this.$toast(this.languages?.creatework_uploadTips);
          return Promise.reject();
        }
      }
      return this.beforeReadImage(file);
    },
    // 图片上传流程
    async afterReadImage(fileArr) {
      for (let i = 0, len = fileArr.length; i < len; i++) {
        const fileItem = fileArr[i];
        let uploadingPromiseResolve = () => {};
        fileArr[i].customerType = 'image';
        fileArr[i].status = 'uploading';
        fileArr[i].message = this.languages?.uploading; // 上传中
        fileArr[i].uploadingPromise = new Promise(
          resolve => (uploadingPromiseResolve = resolve)
        );
        await this.fragmentationUploadImage(fileItem.file)
          .then(url => {
            fileArr[i].isCompress = fileItem.file.isCompress;
            fileArr[i].url = url;
            fileArr[i].status = 'done';
            fileArr[i].message = this.languages?.imageUploadSucc; // 上传成功
          })
          .catch(e => {
            console.log(e);
            fileArr[i].status = 'failed';
            fileArr[i].message = this.languages?.imageUploadFail;
          });
        uploadingPromiseResolve();
      }
    },
    // 上传视频的第一帧
    async uploadFirstFrameImage(base64Str) {
      try {
        const firstFrameImageFile = await binaryToFile(
          base64Str,
          new Date().getTime() + 'firstFrameImage.png',
          'image/png'
        );
        const compressFirstFrameImageFile = await this.compressImage(
          firstFrameImageFile
        );
        const firstFrameImage = await this.fragmentationUploadImage(
          compressFirstFrameImageFile[0]
        );
        return firstFrameImage;
      } catch (e) {
        console.error(e);
        return '';
      }
    },
    // 视频上传流程
    async afterReadVideo(fileArr) {
      let uploadingPromiseResolve = () => {};
      fileArr[0].customerType = 'video';
      fileArr[0].status = 'uploading';
      fileArr[0].message = this.languages?.uploading;
      fileArr[0].uploadingPromise = new Promise(
        resolve => (uploadingPromiseResolve = resolve)
      );
      const videoInfo = await this.getVideoPlayerInfo(fileArr[0].file);
      const firstFrameImage = await this.uploadFirstFrameImage(
        videoInfo.firstFrameBase64Str
      );
      fileArr[0].duration = videoInfo.duration;
      await this.fragmentationUploadVideo(fileArr[0].file)
        .then(url => {
          fileArr[0].isCompress = 1;
          fileArr[0].url = firstFrameImage;
          fileArr[0].videoPath = url;
          fileArr[0].status = 'done';
          fileArr[0].message = this.languages?.videoUploadFail; // TOTO:上传成功
        })
        .catch(e => {
          console.error(e);
          fileArr[0].status = 'failed';
          fileArr[0].message = this.languages?.videoUploadFail;
        });
      uploadingPromiseResolve();
      this.callAndroidDelVideo();
      return;
    },
    // 自定义上传
    afterRead(fileArr) {
      // 视频上传流程
      if (fileArr?.length > 0 && this.checkFileIsVideo(fileArr[0]?.file)) {
        this.afterReadVideo(fileArr);
        return;
      }
      // 图片上传流程
      this.afterReadImage(fileArr);
      return;
    },
    // iOS在SDK中图片和视频上传
    iosUpload() {
      console.log('ios upload');
      // TODO：test
      // (res => {
      //   if (+res.result === 1) {
      //     const data = res?.data || {};
      //     if (data.media_type === 'image') {
      //       this.workFileArr.push({
      //         customerType: 'image',
      //         url: data.image_path,
      //         isCompress: data.is_image_compressed
      //       });
      //     }
      //     if (data.media_type === 'video') {
      //       this.workFileArr.push({
      //         customerType: 'video',
      //         url: data.video_thumbnail_path,
      //         videoPath: data.video_path,
      //         duration: data.video_duration,
      //         isCompress: 1
      //       });
      //     }
      //     this.iOSMaxCount = this.workFileArr.length;
      //   } else {
      //     console.error('iOS upload failed:' + res?.msg);
      //     this.$toast(res?.msg || 'error');
      //   }
      // })({
      //   result: 1,
      //   msg: 'ios提示信息',
      //   data: {
      //     media_type: 'image',
      //     image_path:
      //       `https://gimages.${window.atob('MzdnYW1lcy5jb20=')}/aws_s3/img?s=/support_order/2023/11/EDw8zjIePHqOSjmkWz.jpg`,
      //     is_image_compressed: 1,
      //     video_path: '/support_order_video/2023/11/6CE1w1VDUJ0dNUNPGp.mp4',
      //     video_duration: this.formatVideoDuration(10),
      //     video_thumbnail_path:
      //       `https://gimages.${window.atob('MzdnYW1lcy5jb20=')}/aws_s3/img?s=/support_order/2023/11/ebOZVWrPRa1Bx2QY0g.jpg`
      //   }
      // });
      // return;

      bridge.callSdkMethod(
        'showMediaCompressPicker',
        this.VIDEO_CONFIG,
        res => {
          console.log('iOS upload res', res);
          if (+res.result === 1) {
            const data = res?.data || {};
            if (data.media_type === 'image') {
              this.workFileArr.push({
                customerType: 'image',
                url: data.image_path,
                status: 'done',
                isCompress: Number(data.is_image_compressed)
              });
            }
            if (data.media_type === 'video') {
              this.workFileArr.push({
                customerType: 'video',
                url: data.video_thumbnail_path,
                videoPath: data.video_path,
                duration: this.formatVideoDuration(data.video_duration || 0),
                isCompress: 1,
                status: 'done'
              });
            }
            this.iOSMaxCount = this.workFileArr.length;
          } else {
            this.$toast(res?.msg || 'error');
          }
        }
      );
    },
    // 删除文件前的处理
    beforeDelete() {
      if (this.isIOSUpload) {
        this.iOSMaxCount--;
      }
      return true;
    },
    // 更新上传类型，例如只想上传图片or视频可通过这个方法去更新
    // 如果不支持视频上传，会自动降级到图片上传类型
    updateUploadType(uploadType) {
      if (this.openVideoUploadFunction) {
        this.accept = acceptType[uploadType];
      } else {
        this.accept = acceptType.image;
      }
    }
  }
};
